import {
  Injectable,
  InjectionToken,
  inject,
  type Signal,
  type Type,
} from '@angular/core';
import { Router } from '@angular/router';
import { PERSON_ADMIN_ROUTES } from './person-admin.routes';

/**
 * Where the person drawer goes next: after Edit, a save, a cancel, a delete.
 *
 * On the persons page each of these is a route — the drawer is the outlet of
 * `/dashboard/persons/:id`, and its edit form `/…/:id/edit`. The org chart
 * shows the same drawer in its own sheet, which has no routes to go to and
 * must not send anyone away from the chart. So the drawer and its actions ask
 * this instead of the router, and each host says what the step means there.
 */
export interface PersonAdminFlow {
  /** Edit was pressed in the drawer. */
  edit(personId: string): void;
  /** The form saved the person; the drawer should show them again. */
  saved(personId: string): Promise<void>;
  /** The form was left without saving — `null` when it was creating. */
  cancelled(personId: string | null): void;
  /** The person is gone; nothing of them is left to show. */
  deleted(personId: string): Promise<void>;
  /**
   * What the drawer's delete button says, when the host wants it said
   * differently. In the org chart a plain "Delete" sits among people taken
   * off positions and reads as one more of those; there it names the person.
   */
  readonly deleteLabel?: Signal<string | undefined>;
}

export const PERSON_ADMIN_FLOW = new InjectionToken<PersonAdminFlow>(
  'PERSON_ADMIN_FLOW',
);

/**
 * The form the drawer turns into on Edit, where the drawer is not routed.
 *
 * The form lives in the application, not here — it reads the organization
 * context, and contacts sits beneath organization. So, as with
 * `PERSON_DETAIL_TABS`, the preview declares the hole and the application
 * fills it. The component is handed `personId`.
 */
export const PERSON_EDIT_FORM = new InjectionToken<Type<unknown>>(
  'PERSON_EDIT_FORM',
);

/** The persons page's flow: every step is one of its routes. */
@Injectable()
export class RoutedPersonAdminFlow implements PersonAdminFlow {
  readonly #router = inject(Router);
  readonly #routes = inject(PERSON_ADMIN_ROUTES);

  edit(personId: string): void {
    void this.#router.navigate([...this.#routes.detail(personId), 'edit']);
  }

  async saved(personId: string): Promise<void> {
    await this.#router.navigate([...this.#routes.detail(personId)]);
  }

  cancelled(personId: string | null): void {
    void this.#router.navigate([
      ...(personId ? this.#routes.detail(personId) : this.#routes.list),
    ]);
  }

  async deleted(): Promise<void> {
    await this.#router.navigate([...this.#routes.list]);
  }
}
